<template>
  <div class="servicos-form">
    <form data-testid="servicos-form" @submit.prevent="handleSubmit">
      <div class="flex-container">
        <!-- Nome do Serviço -->
        <div class="flex-item-full">
          <div class="form-group" :class="{ 'has-error': errors.servico_nome }">
            <label>{{ t('Nome do Serviço') }} *</label>
            <input-text
              v-model="formData.servico_nome"
              elem-id="servicos_servico_nome"
              data-testid="servicos-input-nome"
              :placeholder="t('Nome do serviço')"
              maxlength="100"
              class="w-full"
            />
            <small v-if="errors.servico_nome" class="p-error">{{ errors.servico_nome }}</small>
          </div>
        </div>
      </div>

      <div class="flex-container">
        <!-- Categoria -->
        <div class="flex-item-half">
          <div class="form-group" :class="{ 'has-error': errors.servicoscategoria_id }">
            <label>{{ t('Categoria') }} *</label>
            <select-list
              v-model="formData.servicoscategoria_id"
              elem-id="servicos_servicoscategoria_id"
              data-testid="servicos-select-categoria"
              :itens-select="categoriasOptions"
              :placeholder="t('Selecione uma categoria')"
              class="w-full"
            />
            <small v-if="errors.servicoscategoria_id" class="p-error">{{ errors.servicoscategoria_id }}</small>
          </div>
        </div>

        <!-- Valor Padrão -->
        <div class="flex-item-half">
          <div class="form-group" :class="{ 'has-error': errors.servico_valor_padrao }">
            <label>{{ t('Valor Padrão') }} *</label>
            <input-number
              v-model="formData.servico_valor_padrao"
              elem-id="servicos_servico_valor_padrao"
              data-testid="servicos-input-valor-padrao"
              mode="currency"
              currency="BRL"
              locale="pt-BR"
              :min="0"
              class="w-full"
            />
            <small v-if="errors.servico_valor_padrao" class="p-error">{{ errors.servico_valor_padrao }}</small>
          </div>
        </div>
      </div>

    </form>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { log } from '../../utils/DebugLogger';
import { eventBus } from '../../eventBus';
import { useServicos } from '../../composables/useServicos';

import type { ServicosCategorias_CategoriaRow } from '../../contracts/ServicosCategorias_CategoriaRow';

interface ServicoFormData {
  servico_id: number | string | null;
  servico_nome: string;
  servicoscategoria_id: number | string;
  servico_valor_padrao: number;
}

const { t } = useI18n();
const {
  fetchServicoById,
  saveServico,
  fetchCategorias
} = useServicos();

const props = defineProps<{
  servicoId?: number | string | null;
}>();

const emit = defineEmits<{
  (e: 'saved'): void;
  (e: 'cancel'): void;
}>();

const formData = reactive<ServicoFormData>({
  servico_id: null,
  servico_nome: '',
  servicoscategoria_id: '',
  servico_valor_padrao: 0
});

const errors = reactive<Record<string, string>>({});
const isSaving = ref<boolean>(false);
const categorias = ref<ServicosCategorias_CategoriaRow[]>([]);

const categoriasOptions = computed(() =>
  categorias.value.map(cat => ({
    ID: cat.servicoscategoria_id,
    Name: cat.servicoscategoria_nome
  }))
);

const canSave = computed<boolean>(() => {
  return formData.servico_nome.trim() !== '' &&
         formData.servicoscategoria_id !== '' &&
         formData.servico_valor_padrao >= 0;
});

const loadCategorias = async (): Promise<void> => {
  log('ServicosForm', 'loadCategorias', 'Loading categories');

  try {
    const data = await fetchCategorias();
    if (!data) return;
    categorias.value = data;
    log('ServicosForm', 'loadCategorias', 'Categories loaded', data.length);
  } catch (error) {
    log('ServicosForm', 'loadCategorias', 'Error loading categories', error);
    eventBus.emit('globalMessage.error', t('Erro ao carregar categorias'));
  }
};

const initializeForNew = async (): Promise<void> => {
  log('ServicosForm', 'initializeForNew', 'Initializing form for new servico');

  formData.servico_id = null;
  formData.servico_nome = '';
  formData.servicoscategoria_id = '';
  formData.servico_valor_padrao = 0;

  Object.keys(errors).forEach(key => delete errors[key]);

  await loadCategorias();
};

const loadForEdit = async (): Promise<void> => {
  if (!props.servicoId) return;

  log('ServicosForm', 'loadForEdit', 'Loading servico', props.servicoId);

  try {
    await loadCategorias();

    const data = await fetchServicoById(Number(props.servicoId));
    log('ServicosForm', 'loadForEdit', 'Loaded servico', data);

    Object.assign(formData, data);
  } catch (error) {
    log('ServicosForm', 'loadForEdit', 'Error loading servico', error);
    eventBus.emit('globalMessage.error', t('Erro ao carregar serviço'));
  }
};

const validateForm = (): boolean => {
  Object.keys(errors).forEach(key => delete errors[key]);

  let isValid = true;

  if (!formData.servico_nome.trim()) {
    errors.servico_nome = t('Nome é obrigatório');
    isValid = false;
  }

  if (!formData.servicoscategoria_id) {
    errors.servicoscategoria_id = t('Categoria é obrigatória');
    isValid = false;
  }

  if (formData.servico_valor_padrao < 0) {
    errors.servico_valor_padrao = t('Valor não pode ser negativo');
    isValid = false;
  }

  return isValid;
};

const handleSubmit = async (): Promise<void> => {
  log('ServicosForm', 'handleSubmit', 'Submitting form', formData);

  if (!validateForm()) {
    log('ServicosForm', 'handleSubmit', 'Form validation failed');
    return;
  }

  isSaving.value = true;

  try {
    await saveServico({
      servico_nome: formData.servico_nome,
      servico_id: formData.servico_id != null ? Number(formData.servico_id) : undefined,
      servicoscategoria_id: formData.servicoscategoria_id !== '' ? Number(formData.servicoscategoria_id) : undefined,
      servico_valor_padrao: formData.servico_valor_padrao
    });
    log('ServicosForm', 'handleSubmit', 'Servico saved successfully');

    eventBus.emit('globalMessage.success', t('Serviço salvo com sucesso'));
    emit('saved');
  } catch (error) {
    log('ServicosForm', 'handleSubmit', 'Error saving servico', error);
    eventBus.emit('globalMessage.error', t('Erro ao salvar serviço'));
  } finally {
    isSaving.value = false;
  }
};

const handleCancel = (): void => {
  log('ServicosForm', 'handleCancel', 'Form cancelled');
  emit('cancel');
};

watch(() => props.servicoId, async (newId, oldId) => {
  log('ServicosForm', 'watch:servicoId', 'servicoId changed', { oldId, newId });

  if (newId) {
    await loadForEdit();
  } else if (oldId !== null) {
    await initializeForNew();
  }
});

onMounted(async () => {
  log('ServicosForm', 'mounted', 'Component mounted');

  if (props.servicoId) {
    await loadForEdit();
  } else {
    await initializeForNew();
  }
});

defineExpose({
  initializeForNew,
  loadForEdit,
  handleSubmit,
  canSave
});
</script>

<style scoped>
.servicos-form {
  padding: 15px;
}

.flex-container {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-bottom: 20px;
}

.flex-item-half {
  flex: 1 1 calc(50% - 8px);
  min-width: 200px;
}

.flex-item-full {
  flex: 1 1 100%;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
}

.has-error :deep(.p-inputtext),
.has-error :deep(.p-dropdown),
.has-error :deep(.p-inputnumber-input) {
  border-color: #ef4444;
}

.p-error {
  color: #ef4444;
  display: block;
  margin-top: 4px;
}

.w-full {
  width: 100%;
}
</style>
